/**
 * Space views feature (M2 plan §6 M2-20, order `FEATURE_ORDER.spaceViews`): career mode only. Draws what the M2
 * space runtimes simulate, from the `m2` service (M2-22 provides it at order 150):
 * - stations from their deferred models, each built once its station is `ready` (stations.ts);
 * - combat, death and contract wrecks as damaged ship models, salvage parts as props, pooled (wrecks.ts);
 * - ore chunks as one tinted `InstancedMesh` (chunks.ts), rock fragments as asteroid-kit instances (fragments.ts);
 * - the holographic docking guides while the player holds clearance (guides.ts).
 * Everything is pooled and placed in render space each frame (D-011); `frameSpaceViews` allocates nothing once the
 * pools are warm (D-014). Deferred models that have not loaded yet are simply not drawn until they have.
 */
import type { Object3D } from 'three/webgpu'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { SIM_DT } from '../../core/loop'
import { logger } from '../../core/logger'
import { createAsteroidKit } from '../../render/environment'
import type { M2Runtime } from '../../space/contracts'
import { loadM2FxConfig, type M2FxConfig } from '../spacefx/config'
import { buildChunkMesh, createChunkView, disposeChunkView, frameChunks, type ChunkView } from './chunks'
import { createFragmentView, disposeFragmentView, frameFragments, type FragmentView } from './fragments'
import { createGuideView, disposeGuideView, frameGuides, type GuideView } from './guides'
import { createdViews, disposeModelViewPool } from './modelPool'
import { buildStationView, createStationViews, disposeStationViews, frameStations, type StationViews } from './stations'
import { createWreckViews, frameParts, frameWrecks, type WreckViewDeps, type WreckViews } from './wrecks'

/** Live counts for tests and the perf overlay. */
export interface SpaceViewStats {
  stations: number
  wrecks: number
  parts: number
  chunks: number
  fragments: number
  /** Views built so far (wrecks + parts); stops growing once the pools are warm. */
  created: number
  guides: boolean
}

export interface SpaceViewState {
  readonly m2: M2Runtime
  readonly parent: Object3D
  readonly deps: WreckViewDeps
  readonly stations: StationViews
  /** The stations runtime's `version` the station views were built for (M3 R22e: a system switch replaces the list). */
  stationsVersion: number
  readonly wrecks: WreckViews
  readonly chunks: ChunkView
  readonly fragments: FragmentView
  readonly guides: GuideView
}

export interface SpaceViewsFeature extends GameFeature {
  readonly config: M2FxConfig
  readonly stats: SpaceViewStats
  readonly state: SpaceViewState | null
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/** Builds every view container for `m2` (allocates; once per career session). */
export function createSpaceViewState(ctx: GameContext, m2: M2Runtime, config: M2FxConfig): SpaceViewState {
  const space = m2.space
  const parent = ctx.scene
  const deps: WreckViewDeps = {
    lib: ctx.lib,
    models: m2.models,
    defs: space.sim.defs,
    wares: m2.data.wares,
    views: config.views,
    lodScale: ctx.views.lodScale,
  }
  const kit = createAsteroidKit(ctx.lib, space.sim.seed, ctx.quality)
  return {
    m2,
    parent,
    deps,
    stations: createStationViews(space.stations.list.length),
    stationsVersion: space.stations.version ?? 0,
    wrecks: createWreckViews(parent, deps, space.salvage.wrecks.capacity, space.salvage.parts.capacity),
    chunks: createChunkView(m2.data.wares, space.rocks.chunks.capacity),
    fragments: createFragmentView(kit, space.rocks.fragments.capacity, config.views, parent),
    guides: createGuideView(config.guides, m2.data.docking, parent),
  }
}

/** Builds what became buildable since last frame: stations that turned ready, the chunk mesh once its prop loaded. */
function buildPending(ctx: GameContext, s: SpaceViewState, config: M2FxConfig): void {
  const list = s.m2.space.stations.list
  if (s.stations.pending > 0) {
    for (let i = 0; i < list.length; i++) buildStationView(s.stations, list[i], s.m2.models, ctx.lib, s.parent, ctx.views.lodScale)
  }
  if (!s.chunks.mesh) buildChunkMesh(s.chunks, s.m2.models, config.views, s.parent)
}

/** The station list was replaced (a Reach system switch, M3 R22e): drop every station view and size for the new list. */
function resetStationViews(s: SpaceViewState): void {
  const list = s.m2.space.stations.list
  disposeStationViews(s.stations)
  s.stations.instances.length = list.length
  s.stations.instances.fill(null)
  s.stations.pending = list.length
  s.stationsVersion = s.m2.space.stations.version ?? 0
}

/** Per frame (allocation-free once warm). */
export function frameSpaceViews(ctx: GameContext, s: SpaceViewState, config: M2FxConfig, stats: SpaceViewStats, alpha: number): void {
  const space = s.m2.space
  const cam = ctx.rig.pos
  const back = (alpha - 1) * SIM_DT
  // The interpolated render time (as fx/feature.ts renderTime).
  const time = space.sim.time + back
  s.deps.lodScale = ctx.views.lodScale
  if ((space.stations.version ?? 0) !== s.stationsVersion) resetStationViews(s)
  if (s.stations.pending > 0 || !s.chunks.mesh) buildPending(ctx, s, config)
  frameStations(s.stations, space.stations.list, cam)
  frameWrecks(s.wrecks.wrecks, space.salvage.wrecks, back, cam)
  frameParts(s.wrecks.parts, space.salvage.parts, back, cam)
  frameChunks(s.chunks, space.rocks.chunks, config.views, time, back, cam)
  frameFragments(s.fragments, space.rocks.fragments, config.views, time, cam)
  const readout = space.docking.readout
  const station = readout.station >= 0 && readout.station < space.stations.list.length ? space.stations.list[readout.station] : null
  frameGuides(s.guides, station ? station.bay : null, readout, s.m2.data.docking, config.guides, cam)
  stats.stations = s.stations.instances.length - s.stations.pending
  stats.wrecks = s.wrecks.wrecks.live.length
  stats.parts = s.wrecks.parts.live.length
  stats.chunks = s.chunks.drawn
  stats.fragments = s.fragments.drawn
  stats.created = createdViews(s.wrecks.wrecks) + createdViews(s.wrecks.parts)
  stats.guides = s.guides.visible
}

export function disposeSpaceViewState(s: SpaceViewState): void {
  disposeStationViews(s.stations)
  disposeModelViewPool(s.wrecks.wrecks)
  disposeModelViewPool(s.wrecks.parts)
  disposeChunkView(s.chunks)
  disposeFragmentView(s.fragments)
  disposeGuideView(s.guides)
}

export function createSpaceViewsFeature(config: M2FxConfig = loadM2FxConfig()): SpaceViewsFeature {
  let state: SpaceViewState | null = null
  const stats: SpaceViewStats = { stations: 0, wrecks: 0, parts: 0, chunks: 0, fragments: 0, created: 0, guides: false }

  /** The `m2` service, once the career feature provides it (career mode only). */
  function attach(ctx: GameContext): void {
    if (state || ctx.mode !== 'career') return
    const m2 = ctx.services.maybe('m2')
    if (!m2) return
    state = createSpaceViewState(ctx, m2, config)
    logger.info('Space views: attached to the M2 runtime')
  }

  const feature: SpaceViewsFeature = {
    name: 'spaceViews',
    order: FEATURE_ORDER.spaceViews,
    config,
    stats,
    get state(): SpaceViewState | null {
      return state
    },
    init(ctx: GameContext): void {
      attach(ctx)
    },
    frame(ctx: GameContext, f: FrameInfo): void {
      if (!state) {
        attach(ctx)
        if (!state) return
      }
      frameSpaceViews(ctx, state, config, stats, f.alpha)
    },
    dispose(): void {
      if (state) disposeSpaceViewState(state)
      state = null
    },
  }
  return feature
}

const feature: SpaceViewsFeature = createSpaceViewsFeature()
export default feature
